import { FC } from 'react';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { Box, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import { cloneDeep, remove } from 'lodash';
import Text from '@/components/text';
import { ThreadMessageModel } from '@/lib/models/thread/thread-message.model';
import classNames from 'classnames';
import 'github-markdown-css/github-markdown-light.css';
import CodeBlock from './code-block';
import AnchorTag from './anchor-tag';

interface Props {
  message: ThreadMessageModel;
}

const useStyles = createUseStyles((theme: Theme) => ({
  messageContent: {
    marginBottom: 4,
    minWidth: 0,
    maxWidth: '100%',
    overflowWrap: 'break-word',
    textWrap: 'wrap',
    wordBreak: 'break-word',
    '&.markdown-body': {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
      '& h1,h2,h3': {
        fontSize: '1.25em',
      },
      '& code': {
        backgroundColor: '#818b984a',
      },
      '& pre': {
        padding: 2,
        backgroundColor:
          theme.palette.mode === 'dark'
            ? theme.palette.background.sidebar
            : theme.palette.grey[400],
        borderRadius: theme.shape.borderRadius,
        '& code': {
          backgroundColor: 'transparent !important',
          textWrap: 'auto',
          wordWrap: 'break-word',
        },
      },
      '& table': {
        borderRadius: 3,
        '& th': {
          backgroundColor: theme.palette.mode === 'dark' ? '#4a4a4a' : '#a7a7a7',
          textAlign: 'left',
        },
        '& th, td': {
          padding: '8px 12px',
        },
        '& tr': {
          background: '#3e3e3e4a',
        },
      },
      '& li.task-list-item': {
        listStyleType: 'circle',
      },
    },
  },
  userMessage: {
    whiteSpace: 'pre-wrap',
  },
}));

const getRehypeSanitizeSchema = () => {
  const schema = cloneDeep(defaultSchema);
  const disallowedTags = ['img', 'input', 'picture', 'source'];
  schema.tagNames = remove(schema.tagNames!, (tag) => !disallowedTags.includes(tag));
  return schema;
};

const MessageContent: FC<Props> = ({ message }) => {
  const { content = '', component, role } = message;
  const safeContent = String(content).trim();
  const styles = useStyles();

  if (component) {
    return <Box className={styles.messageContent}>{component}</Box>;
  }

  if (!safeContent) {
    return (
      <Box className={styles.messageContent}>
        <Text italic>&lt;message not available&gt;</Text>
      </Box>
    );
  }

  // user input should not support markdown/html and be escaped for security
  if (role === 'user') {
    return (
      <Box className={styles.messageContent}>
        <Box className={styles.userMessage}>{safeContent}</Box>
      </Box>
    );
  }

  const compClass = classNames(styles.messageContent, 'markdown-body');

  return (
    <Box className={compClass}>
      <Markdown
        children={safeContent}
        remarkPlugins={[remarkBreaks, remarkGfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, getRehypeSanitizeSchema()]]}
        components={{
          code: (props) => <CodeBlock {...props} />,
          a: (props) => <AnchorTag {...props} />,
        }}
      />
    </Box>
  );
};

export default MessageContent;
